import { Fullstory } from 'derivita/util/log/fullstory';
import {
  register,
  resolve,
  serviceExists,
} from '../../service_locator/container';
import { ProductAnalytics } from '../service/analytics';
import { CompositeProductAnalytics } from './composite';
import { OtlpAnalytics, OtlpAnalyticsConfig } from '../otlp/otlp_analytics';
import {
  firebaseAnalyticsAuthHeaders,
  ltiSessionAuthHeaders,
} from './auth_headers';
import { resolveSessionId } from './session_id';

// Apps wire an OtlpAnalyticsConfig via configureAnalytics() before the first
// captureEvent fires so that OTLP events carry the right service.name /
// user.id / customer / session.id attributes. If no config is supplied (e.g.
// storybook, tests that forget to wire it), we fall back to FullStory-only.
//
// Call order matters: configureAnalytics() must run before the first service()
// call, because service() memoises its composite via the service locator. A
// later configureAnalytics() is ignored — the already-registered composite
// does not rebuild itself, so we surface that mistake via console.warn.
let otlpConfig: OtlpAnalyticsConfig | undefined;
const readyCallbacks: Array<() => void> = [];

export function configureAnalytics(config: OtlpAnalyticsConfig): void {
  if (serviceExists(ProductAnalytics)) {
    console.warn(
      '[analytics] configureAnalytics called after service() resolved; OTLP config will be ignored'
    );
    return;
  }
  otlpConfig = config;
  runReadyCallbacks();
}

// True once capturing an event can no longer lock the page to FullStory only.
export function isAnalyticsReady(): boolean {
  return otlpConfig !== undefined || serviceExists(ProductAnalytics);
}

export function whenAnalyticsReady(callback: () => void): void {
  if (isAnalyticsReady()) {
    callback();
  } else {
    readyCallbacks.push(callback);
  }
}

function runReadyCallbacks() {
  for (const callback of readyCallbacks.splice(0)) {
    callback();
  }
}

export function service(): ProductAnalytics {
  if (serviceExists(ProductAnalytics)) {
    return resolve(ProductAnalytics);
  }

  const delegates: ProductAnalytics[] = [new Fullstory()];
  if (otlpConfig) {
    delegates.push(new OtlpAnalytics(otlpConfig));
  }
  const composite = new CompositeProductAnalytics(delegates);
  register(ProductAnalytics, composite);
  runReadyCallbacks();
  return composite;
}

// wireSessionAnalytics / wireFirebaseAnalytics are the usual call sites for
// apps: they assemble an OtlpAnalyticsConfig from the handful of values each
// app has on hand (a session key or a Firebase-token getter, plus a service
// name). Both delegate to configureAnalytics and therefore inherit its
// call-order-matters semantics.
export interface WireOptions {
  readonly userId?: string;
  // LTI session key to authenticate with while getToken has no token or
  // rejects, so a page can wire analytics before its Firebase sign-in completes.
  readonly fallbackSessionKey?: string;
}

export function wireSessionAnalytics(
  sessionKey: string,
  serviceName: string,
  opts: WireOptions = {}
): void {
  configureAnalytics({
    hrpcUrl: '/api/v2/',
    getAuthHeaders: ltiSessionAuthHeaders(sessionKey),
    serviceName,
    userId: opts.userId,
    sessionId: resolveSessionId(),
    deploymentEnvironment: window.location.hostname,
  });
}

export function wireFirebaseAnalytics(
  getToken: () => Promise<string | null | undefined>,
  serviceName: string,
  opts: WireOptions = {}
): void {
  configureAnalytics({
    hrpcUrl: '/api/v2/',
    getAuthHeaders: firebaseAnalyticsAuthHeaders(
      getToken,
      opts.fallbackSessionKey
    ),
    serviceName,
    userId: opts.userId,
    sessionId: resolveSessionId(),
    deploymentEnvironment: window.location.hostname,
  });
}

// Test-only: clears module state so each test case sees a fresh
// configureAnalytics → service() lifecycle. Not intended for production use.
export function _resetForTests(): void {
  otlpConfig = undefined;
  readyCallbacks.length = 0;
}
